import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import {
  Droplets, LogOut, Plus, Pencil, Trash2, Package, ClipboardList, Lock, Loader2, Phone, MapPin,
} from "lucide-react";
import { getProducts, fmtPrice } from "@/lib/api";
import {
  getToken, clearToken, adminLogin, adminCheck, adminDeleteProduct, adminListOrders, resolveImage,
  adminGenerateAwb, adminDownloadAwb, adminListReviews, adminDeleteReview,
  adminGenerateInvoice, adminDownloadInvoice, adminUpdateOrderCustomer,
} from "@/lib/adminApi";
import { AwbCustomerDialog } from "@/pages/admin/AwbCustomerDialog";
import { FileText, Download, Loader2 as Spin, Star, Trash2 as TrashR, Receipt, Settings } from "lucide-react";
import { ProductForm } from "@/pages/admin/ProductForm";
import { SiteSettingsForm } from "@/pages/admin/SiteSettingsForm";

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    adminCheck().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);

  if (authed === null) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-eco" /></div>;
  }
  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;
  return <AdminDashboard onLogout={() => { clearToken(); setAuthed(false); }} />;
}

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(password);
      toast.success("Autentificat");
      onLogin();
    } catch {
      toast.error("Parolă incorectă");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border-2 border-slate-100 rounded-2xl shadow-xl p-8">
        <div className="flex items-center gap-2.5 mb-6">
          <span className="bg-eco rounded-xl p-2"><Droplets className="h-6 w-6 text-white" /></span>
          <span className="font-display font-black text-navy text-lg">Panou Admin</span>
        </div>
        <label className="block text-sm font-bold text-navy mb-1.5">Parolă admin</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="inp pl-9"
            placeholder="Introdu parola"
            data-testid="admin-password-input"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full mt-5 bg-eco hover:bg-green-700 disabled:opacity-60 text-white font-display font-black py-3 rounded-xl inline-flex items-center justify-center gap-2"
          data-testid="admin-login-button"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} INTRĂ
        </button>
      </form>
    </div>
  );
}

function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("products");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: products = [], refetch: refetchProducts } = useQuery({ queryKey: ["admin-products"], queryFn: getProducts });
  const { data: orders = [], refetch: refetchOrders, isError: ordersErr } = useQuery({ queryKey: ["admin-orders"], queryFn: adminListOrders });
  const { data: reviews = [], refetch: refetchReviews } = useQuery({ queryKey: ["admin-reviews"], queryFn: adminListReviews });

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (p) => { setEditing(p); setFormOpen(true); };

  const remove = async (p) => {
    if (!window.confirm(`Ștergi produsul "${p.name}"?`)) return;
    try {
      await adminDeleteProduct(p.id);
      toast.success("Produs șters");
      refetchProducts();
    } catch {
      toast.error("Eroare la ștergere");
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="admin-dashboard">
      <div className="bg-navy">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="bg-eco rounded-xl p-2"><Droplets className="h-5 w-5 text-white" /></span>
            <span className="font-display font-black text-white text-lg">Panou Admin — DeterShop</span>
          </div>
          <button onClick={onLogout} className="text-white/80 hover:text-white text-sm font-bold inline-flex items-center gap-2" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Ieși
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2 mb-6">
          <TabBtn active={tab === "products"} onClick={() => setTab("products")} icon={Package} label={`Produse (${products.length})`} testid="tab-products" />
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} icon={ClipboardList} label={`Comenzi (${orders.length})`} testid="tab-orders" />
          <TabBtn active={tab === "reviews"} onClick={() => setTab("reviews")} icon={Star} label={`Recenzii (${reviews.length})`} testid="tab-reviews" />
          <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} icon={Settings} label="Setări site" testid="tab-settings" />
        </div>

        {tab === "settings" && (
          <div>
            <h2 className="font-display font-black text-navy text-xl mb-4">Setări site</h2>
            <SiteSettingsForm />
          </div>
        )}

        {tab === "products" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display font-black text-navy text-xl">Produse</h2>
              <button onClick={openNew} className="bg-eco hover:bg-green-700 text-white font-bold px-4 py-2.5 rounded-xl inline-flex items-center gap-2" data-testid="add-product-button">
                <Plus className="h-4 w-4" /> Adaugă produs
              </button>
            </div>
            <div className="grid gap-3">
              {products.map((p) => (
                <div key={p.id} className="bg-white border-2 border-slate-100 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-product-${p.slug}`}>
                  <img src={resolveImage(p.image)} alt={p.name} className="h-16 w-16 object-contain bg-slate-50 rounded-lg shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-navy truncate">{p.name} {p.badge && <span className="ml-1 text-[10px] bg-red-600 text-white px-1.5 py-0.5 rounded align-middle">{p.badge}</span>}</p>
                    <p className="text-sm text-muted-foreground truncate">{p.description}</p>
                    <p className="text-sm font-bold text-navy">{fmtPrice(p.price)} lei {p.old_price && <span className="text-slate-400 line-through font-normal">{fmtPrice(p.old_price)}</span>} · {p.volume}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => openEdit(p)} className="p-2.5 rounded-lg bg-slate-100 hover:bg-slate-200" data-testid={`edit-product-${p.slug}`}><Pencil className="h-4 w-4 text-navy" /></button>
                    <button onClick={() => remove(p)} className="p-2.5 rounded-lg bg-red-50 hover:bg-red-100" data-testid={`delete-product-${p.slug}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div>
            <h2 className="font-display font-black text-navy text-xl mb-4">Comenzi</h2>
            {ordersErr ? (
              <p className="text-muted-foreground">Nu s-au putut încărca comenzile.</p>
            ) : orders.length === 0 ? (
              <p className="text-muted-foreground">Nicio comandă încă.</p>
            ) : (
              <div className="grid gap-3">
                {orders.map((o) => (
                  <div key={o.id} className="bg-white border-2 border-slate-100 rounded-xl p-4" data-testid={`admin-order-${o.id.slice(0,8)}`}>
                    <div className="flex flex-wrap justify-between gap-2">
                      <div>
                        <p className="font-display font-bold text-navy">{o.customer.name}</p>
                        <p className="text-sm text-muted-foreground inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {o.customer.phone}</p>
                        <p className="text-sm text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {o.customer.address}, {o.customer.city}, {o.customer.county}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-display font-black text-navy text-lg">{fmtPrice(o.total)} lei</p>
                        <p className="text-[10px] text-muted-foreground">total cu transport</p>
                        <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("ro-RO")}</p>
                        <span className="text-xs bg-gold/20 text-navy px-2 py-0.5 rounded font-bold">Ramburs</span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <OrderAwb order={o} onDone={refetchOrders} />
                    </div>
                    <div className="mt-2">
                      <OrderInvoice order={o} onDone={refetchOrders} />
                    </div>
                    <div className="mt-3 border-t pt-3 space-y-1">
                      {o.items.map((i, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-slate-700">{i.name} × {i.quantity}</span>
                          <span className="font-bold">{fmtPrice(i.price * i.quantity)} lei</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm pt-1 border-t mt-1">
                        <span className="text-muted-foreground">Subtotal produse</span>
                        <span className="font-bold">{fmtPrice(o.subtotal)} lei</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Transport</span>
                        <span className="font-bold">{o.shipping === 0 ? "Gratuit" : `${fmtPrice(o.shipping)} lei`}</span>
                      </div>
                      <div className="flex justify-between text-sm font-display font-black text-navy">
                        <span>Total de plată</span>
                        <span>{fmtPrice(o.total)} lei</span>
                      </div>
                      {o.customer.notes && <p className="text-xs text-muted-foreground italic pt-1">Observații: {o.customer.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "reviews" && (
          <div>
            <h2 className="font-display font-black text-navy text-xl mb-4">Recenzii</h2>
            {reviews.length === 0 ? (
              <p className="text-muted-foreground">Nicio recenzie.</p>
            ) : (
              <div className="grid gap-3">
                {reviews.map((r) => {
                  const prod = products.find((p) => p.id === r.product_id);
                  return (
                    <div key={r.id} className="bg-white border-2 border-slate-100 rounded-xl p-4 flex items-start gap-4" data-testid={`admin-review-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display font-bold text-navy">{r.author}</span>
                          <span className="flex">{[1,2,3,4,5].map((i) => (
                            <Star key={i} className={`h-3.5 w-3.5 ${i <= r.rating ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200"}`} />
                          ))}</span>
                          <span className="text-xs bg-slate-100 text-navy px-2 py-0.5 rounded font-bold">{prod ? prod.name : r.product_id}</span>
                        </div>
                        <p className="text-sm text-slate-700 mt-1">{r.comment}</p>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString("ro-RO")}</p>
                      </div>
                      <button
                        onClick={async () => { if (window.confirm("Ștergi recenzia?")) { await adminDeleteReview(r.id); toast.success("Recenzie ștearsă"); refetchReviews(); } }}
                        className="p-2.5 rounded-lg bg-red-50 hover:bg-red-100 shrink-0"
                        data-testid={`delete-review-${r.id}`}
                      >
                        <TrashR className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <ProductForm
        open={formOpen}
        product={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => { refetchProducts(); }}
      />
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 rounded-xl font-display font-bold text-sm inline-flex items-center gap-2 transition-colors ${
        active ? "bg-navy text-white" : "bg-white text-navy border-2 border-slate-100 hover:border-navy/20"
      }`}
      data-testid={testid}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function OrderAwb({ order, onDone }) {
  const [busy, setBusy] = useState(false);
  const short = order.id.slice(0, 8);
  const hasAwb = order.cargus && order.cargus.barcode;
  const [dialogOpen, setDialogOpen] = useState(false);

  const generate = async (customer) => {
    setBusy(true);
    try {
      await adminUpdateOrderCustomer(order.id, customer);
      const res = await adminGenerateAwb(order.id);
      toast.success(`AWB generat: ${res.barcode}`);
      setDialogOpen(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la generarea AWB");
      onDone();
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      await adminDownloadAwb(order.id, order.cargus.barcode);
    } catch (e) {
      toast.error("Eroare la descărcarea AWB");
    } finally {
      setBusy(false);
    }
  };

  if (hasAwb) {
    return (
      <div className="flex flex-wrap items-center gap-2 border-t pt-3">
        <span className="text-xs font-bold text-eco inline-flex items-center gap-1">
          <FileText className="h-4 w-4" /> AWB Cargus: {order.cargus.barcode}
        </span>
        <button
          onClick={download}
          disabled={busy}
          className="ml-auto bg-navy hover:bg-navy-700 text-white text-sm font-bold px-4 py-2 rounded-xl inline-flex items-center gap-2 disabled:opacity-60"
          data-testid={`download-awb-${short}`}
        >
          {busy ? <Spin className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Descarcă AWB PDF
        </button>
      </div>
    );
  }

  return (
    <div className="flex justify-end border-t pt-3">
      <button
        onClick={() => setDialogOpen(true)}
        disabled={busy}
        className="bg-eco hover:bg-green-700 text-white text-sm font-bold px-4 py-2 rounded-xl inline-flex items-center gap-2 disabled:opacity-60"
        data-testid={`generate-awb-${short}`}
      >
        {busy ? <Spin className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Generare AWB
      </button>
      <AwbCustomerDialog open={dialogOpen} order={order} busy={busy} onClose={() => setDialogOpen(false)} onConfirm={generate} />
    </div>
  );
}

function OrderInvoice({ order, onDone }) {
  const [busy, setBusy] = useState(false);
  const short = order.id.slice(0, 8);
  const hasInv = order.invoice && order.invoice.number;

  const generate = async () => {
    setBusy(true);
    try {
      const res = await adminGenerateInvoice(order.id);
      toast.success(`Factură generată: ${res.series} ${res.number}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la generarea facturii");
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      await adminDownloadInvoice(order.id, `${order.invoice.series}-${order.invoice.number}`);
    } catch (e) {
      toast.error("Eroare la descărcarea facturii");
    } finally {
      setBusy(false);
    }
  };

  if (hasInv) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-indigo-600 inline-flex items-center gap-1">
          <Receipt className="h-4 w-4" /> Factură: {order.invoice.series} {order.invoice.number}
        </span>
        <button
          onClick={download}
          disabled={busy}
          className="ml-auto bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-4 py-2 rounded-xl inline-flex items-center gap-2 disabled:opacity-60"
          data-testid={`download-invoice-${short}`}
        >
          {busy ? <Spin className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Vezi factura PDF
        </button>
      </div>
    );
  }

  return (
    <div className="flex justify-end">
      <button
        onClick={generate}
        disabled={busy}
        className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-4 py-2 rounded-xl inline-flex items-center gap-2 disabled:opacity-60"
        data-testid={`generate-invoice-${short}`}
      >
        {busy ? <Spin className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />} Generează factură
      </button>
    </div>
  );
}
